import React from 'react';

import styles from './DialogEditContactInfo.module.css';

import { getAddress, getFullName } from '../../../services/getPatientAtribute';
import { getEnvironmentApiUrl } from '../../../services/General';

import { useRef } from 'react';
import useHttp from '../../../hooks/useHttp';

import Input from '../../UI/Input/Input';
import AutocompleteAddress from '../AutocompleteAddress/AutocompleteAddress';

import { Address, Patient } from '../../../interfaces/Patient';

import { useTranslation } from 'react-i18next';
import usePatientStore from '../../../stores/patient-store';
import { PlaceDetails } from '../AutocompleteAddress/useAddressAutocomplete';

const DialogEditContactInfo: React.FC<{
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>,
}> = ({ setShowModal }) => {

    const { t } = useTranslation();

    const updatePatient = usePatientStore(state => state.updatePatient);
    const { sendRequest: putPatientData } = useHttp();
    const { sendRequest: updateAddress } = useHttp<{ address: Address }>();

    const patient = usePatientStore(state => state.patient);

    const mobilePhone = useRef(patient?.mobile_phone ?? '');
    const homePhone = useRef(patient?.home_phone ?? '');
    const email = useRef(patient?.email ?? '');
    const addressRef = useRef<Address | null>(null);

    const handleChangeMobilePhone = (evt: React.FormEvent) =>
        mobilePhone.current = (evt.target as HTMLInputElement).value;

    const handleChangeHomePhone = (evt: React.FormEvent) =>
        homePhone.current = (evt.target as HTMLInputElement).value;

    const handleChangeEmail = (evt: React.FormEvent) =>
        email.current = (evt.target as HTMLInputElement).value;

    const handleChangePlace = (placeDetails: PlaceDetails) => {
        const streetNumberAddress = `${placeDetails.components.street_number ? ' ' + placeDetails.components.street_number : ''}`;
        const apartmentNumber = `${placeDetails.components.subpremise ? ', ' + placeDetails.components.subpremise : ''}`;
        const address: Address = {
            full_address: placeDetails.formattedAddress,
            country: placeDetails.components.country,
            state: placeDetails.components.administrative_area_level_1,
            city: placeDetails.components.locality,
            zip: placeDetails.components.postal_code,
            street: placeDetails.components.route + streetNumberAddress + apartmentNumber,
            town: {
                name: placeDetails.components.locality,
                postal_code: placeDetails.components.postal_code
            },
        };
        addressRef.current = address;
    };

    const updateEmailPhone = (patientToUpdate: Patient) => {

        if (email.current === patientToUpdate.email &&
            homePhone.current === patientToUpdate.home_phone &&
            mobilePhone.current === patientToUpdate.mobile_phone
        ) {
            setShowModal(false);
            return;
        }

        const body = {
            email: email.current,
            home_phone: homePhone.current,
            mobile_phone: mobilePhone.current,
        };

        putPatientData({
            url: getEnvironmentApiUrl() + `patient/${patientToUpdate.id}`,
            method: 'PUT',
            body: body,
        }, () => updatePatient(body));
    };

    const handleClickSave = (e: React.FormEvent) => {

        e.preventDefault();

        if (!patient) return;

        updateEmailPhone(patient);

        if (addressRef.current === null) return;

        const newAddresses = [...patient.addresses ?? []];
        newAddresses[0] = { ...newAddresses[0], ...addressRef.current };

        if (patient.addresses.length > 0) {
            updateAddress({
                url: getEnvironmentApiUrl() + `patient/${patient.id}/address/${patient.addresses[0].id}`,
                method: 'PUT',
                body: addressRef.current
            }, () => {
                updatePatient({ addresses: newAddresses });
            });
        }
        else {
            updateAddress({
                url: getEnvironmentApiUrl() + `patient/${patient.id}/address`,
                method: 'POST',
                body: addressRef.current
            }, (address) => {
                updatePatient({ addresses: [address.address] });
            });
        }

        setShowModal(false);
    };

    return (
        <form className='dialog' onSubmit={handleClickSave}>
            <div className={styles.dialogHeader}>
                <h1 className='dialogTitle'>{t('edit_contact_info')}</h1>
                <h2 className='dialogSubTitle'>{getFullName(patient)}</h2>
            </div>
            <div>
                <h3 className={styles.infoRowTitle}>{t('phones')}</h3>
                <div className={styles.inputContainer}>
                    <Input
                        label={t('mobile_phone')}
                        defaultValue={patient?.mobile_phone ?? ''}
                        onChange={handleChangeMobilePhone}
                    />
                    <Input
                        label={t('phone')}
                        defaultValue={patient?.home_phone ?? ''}
                        onChange={handleChangeHomePhone}
                    />
                </div>
            </div>
            <div>
                <h3 className={styles.infoRowTitle}>{t('email')}</h3>
                <div className={styles.inputContainer}>
                    <Input
                        label={t('email')}
                        defaultValue={patient?.email ?? ''}
                        onChange={handleChangeEmail}
                    />
                </div>
            </div>
            <div>
                <h3 className={styles.infoRowTitle}>{t('address')}</h3>
                <div className={styles.inputContainer}>
                    <AutocompleteAddress
                        defaultValue={getAddress(patient)}
                        handleChangePlace={handleChangePlace}
                    />
                </div>
            </div>
            <div className={styles.buttonsContainer}>
                <button type='button' onClick={() => setShowModal(false)} className={`button ${styles.cancel}`}>
                    {t('cancel')}
                </button>
                <button type='submit' className={`button ${styles.confirm}`}>
                    {t('save')}
                </button>
            </div>
        </form>
    );
};

export default DialogEditContactInfo;